/* eslint-disable no-underscore-dangle */
import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  useCallback,
} from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Button, IconButton, Paper } from '@mui/material';
// import SinglePointMode from './modes/draw_single_point';

import { makeStyles } from '@mui/styles';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

// import { store } from 'contexts/Map/store';
import { FDContext } from 'contexts/Map/fdStore';

import * as turf from '@turf/turf';

import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';

import DrawAssistedRectangle from '@geostarters/mapbox-gl-draw-rectangle-assisted-mode';
import tilebelt from '@mapbox/tilebelt';
import {
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
  KeyboardArrowUp,
  // LineGlobeIcon,
} from '@mui/icons-material';

import {
  addBaseMapLayers,
  addPlaceLabelLayers,
  mapStyle,
  turnOffBaseMapLayers,
  turnOnBaseMapLayers,
  getTilesForCurrentBounds,
  locationObj,
  getPolygonInfoWindowPoint,
} from 'helpers/mapUtils';

import MAXZOOM from 'constants/map-constants';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import {
  setPoint,
  setPolygon,
  setTile,
  setFieldId,
  setTiles,
  setAPIFD,
  setDrawModeFD,
} from 'contexts/Map/action';

import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { LineGlobeIcon } from 'icons';

import SinglePointMode from './modes/draw_single_point';
import {
  getMapBaseLayerList,
  getMapPlaceLabelLayerList,
  getMapStyle,
} from '../../api/fieldDelineation/mapApi';

mapboxgl.accessToken =
  'pk.eyJ1IjoiZ2lyaXNocGFsbGFnYXR0aSIsImEiOiJja2h6NGVuc20wYndjMnlxaDExbzlsa3B2In0.e94ZfcoPleAA2nqjeX10ag';
// 'pk.eyJ1Ijoicm9oaXRzaGV0dHkwMCIsImEiOiJjanpmOXVoOXowOXF6M2lsdG95NmF5dWEzIn0.yuI5HCaw8Ka2-hMQM5RG4A';

let heightOfTheWindow = window.innerHeight;

window.addEventListener('resize', () => {
  heightOfTheWindow = window.innerHeight;
});

const useStyles = makeStyles({
  mapContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  mapAndControlsContainer: {
    flex: 1,
    position: 'relative',
    left: '-1px',
    top: '-1px',
  },
  hoverDisplayContainer: {
    position: 'absolute',
    left: 60,
    top: 0,
    zIndex: 1,
    minHeight: 220,
    paddingLeft: 8,
    minWidth: 160,
    textAlign: 'left',
    color: '#fff',
  },
  shadowGenerator: {
    padding: 30,
    zIndex: 1,
    position: 'relative',
    top: -60,
    boxShadow: '-54px 72px 138px 62px rgba(0,0,0,0.85)',
  },
  hoverContent: {
    position: 'relative',
    zIndex: 2,
  },
  globeIconContainer: {
    position: 'absolute',
    right: 8,
    bottom: 12,
    zIndex: 1,
    height: 48,
    width: 48,
    '@media (max-width: 600px)': {
      bottom: 56,
    },
  },
  globebutton: {
    width: 48,
    height: 48,
  },
  checkboxContainer: {
    position: 'absolute',
    left: 8,
    bottom: 38,
    zIndex: 1000,
    color: 'black',
    backgroundColor: 'white',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0px 8px',
    '& .MuiCheckbox-root': {
      color: '#06b075', // DigiFarm green
      '&.Mui-checked': {
        color: '#06b075', // DigiFarm green
      },
    },
    '& .MuiCheckbox-colorSecondary.Mui-checked': {
      color: 'white', // Color of the check icon
    },
    '& .MuiFormControlLabel-label': {
      fontWeight: 'bold',
    },
  },
  iconRoot: {
    fill: 'none',
  },
  clearButtonContainer: {
    position: 'absolute',
    right: 60,
    bottom: 12,
    // display: 'flex',
    // width: '100%',
    // justifyContent: 'center',
  },
  clearButton: {
    backgroundColor: '#1D2023',
    fontSize: 14,
  },
  drawerToggleContainer: {
    position: 'absolute',
    right: 8,
    top: '40vh',
    backgroundColor: '#fff',
    borderRadius: 8,
    '@media (max-width: 600px)': {
      display: 'none',
    },
  },
  collapseDrawerButton: {
    backgroundColor: '#000',
    color: '#fff',
    borderRadius: 8,
    padding: 8,
    '&:hover': {
      color: '#999',
    },
  },
  collapseDrawerButtonMobile: {
    backgroundColor: '#000',
    color: '#fff',
    borderRadius: 8,
    padding: 8,
  },
  drawerToggleMobileOpen: {
    position: 'absolute',
    right: 68,
    top: heightOfTheWindow - 340,
    backgroundColor: '#fff',
    borderRadius: 8,
    '@media (min-width: 600px)': {
      display: 'none',
    },
  },
  drawerToggleMobileClosed: {
    position: 'absolute',
    right: 68,
    bottom: 64,
    backgroundColor: '#fff',
    borderRadius: 8,
    '@media (min-width: 600px)': {
      display: 'none',
    },
  },
});

const DrawContext = createContext();
export const MapContext = createContext();
export const PopupContext = createContext();

// Update the clearAllMapPopups function to work with context
export const clearAllMapPopups = (map, mapTopPopupRef, setMapTopPopupRef) => {
  // First reset the popup reference in PopupContext
  if (setMapTopPopupRef) {
    setMapTopPopupRef(null);
  }

  // Then remove any popups on the map
  if (map) {
    // Try 1: Remove via _popups collection
    if (map._popups && map._popups.length) {
      // Make a copy of the array to avoid modification during iteration
      const popups = [...map._popups];
      popups.forEach((popup) => popup.remove());
    }

    // Try 2: Find and remove any popup elements directly from the DOM
    const popupElements = document.getElementsByClassName('mapboxgl-popup');
    if (popupElements.length > 0) {
      Array.from(popupElements).forEach((element) => {
        element.remove();
      });
    }
  }
};

// Add a new context-aware version that can be called without parameters
export const useClearMapPopups = () => {
  const { map } = useContext(MapContext);
  const { mapTopPopupRef, setMapTopPopupRef } = usePopupContext();

  return useCallback(() => {
    clearAllMapPopups(map, mapTopPopupRef, setMapTopPopupRef);
  }, [map, mapTopPopupRef, setMapTopPopupRef]);
};

export const useDrawContext = () => useContext(DrawContext);
// console.log('useDrawContext: ', useDrawContext);
export const useMapContext = () => useContext(MapContext);
export const usePopupContext = () => useContext(PopupContext);

export const DrawProvider = ({ children }) => {
  const [draw, setDraw] = useState(null); // Initialize draw state

  return (
    <DrawContext.Provider value={{ draw, setDraw }}>
      {children}
    </DrawContext.Provider>
  );
};

export const MapProvider = ({ children }) => {
  const [map, setMap] = useState(null);
  const [zoomLevel, setZoom] = useState(13);
  const [longitude, setLongitude] = useState(11.18047);
  const [latitude, setLatitude] = useState(60.74627);
  const [mapTopPopupRef, setMapTopPopupRef] = useState(null);

  useEffect(() => {
    if (map) {
      map.on('moveend', () => {
        const center = map.getCenter();
        setLongitude(center.lng);
        setLatitude(center.lat);
        setZoom(map.getZoom());
      });
    }
  }, [map]);

  return (
    <MapContext.Provider
      value={{ map, setMap, zoomLevel, longitude, latitude }}
    >
      <PopupContext.Provider value={{ mapTopPopupRef, setMapTopPopupRef }}>
        {children}
      </PopupContext.Provider>
    </MapContext.Provider>
  );
};

const Map = (props) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const {
    state: {
      mode,
      // drawMode: mapDrawMode,
      feature,
      point,
      polygon,
      location,
      dataLayerFD,
      tileURL,
      tiles,
      tileUrlCounter,
      fieldId,
    },
    dispatch,
  } = useContext(FDContext);

  const classes = useStyles();

  const {
    layer,
    zoom,
    setZoom,
    toggleRightDrawer,
    isRightDrawerOpen,
    dataLayerConfig,
  } = props;

  const [lng, setLng] = useState(11.1804734);
  const [lat, setLat] = useState(60.746268);

  const { draw, setDraw } = useDrawContext();
  const { map, setMap } = useMapContext();
  const { mapTopPopupRef, setMapTopPopupRef } = usePopupContext();

  const modeRef = useRef(null);
  const drawModeRef = useRef(null);
  const [mapDrawMode, setMapDrawMode] = useState(dataLayerFD.drawMode);
  const [showClearButton, setShowClearButton] = useState(false);

  const [fieldIdLocal, setFieldIdLocal] = useState('');
  const [localLocation, setLocalLocation] = useState(location);
  const [mapBaseLayer, setMapBaseLayer] = useState([]);
  const [mapPlaceLabelLayer, setMapPlaceLabelLayer] = useState([]);

  let onCreateListener;

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const dataLayermapDrawMode = dataLayerFD.drawMode;

    if (map && dataLayermapDrawMode) {
      // Always remove existing draw control first
      if (draw && draw._map) {
        try {
          map.removeControl(draw);
        } catch (e) {
          console.log('Error removing draw control:', e);
        }
      }

      const Draw = new MapboxDraw({
        displayControlsDefault: false,
        modes: {
          draw_single_point: SinglePointMode,
          draw_rectangle: DrawRectangle,
          ...MapboxDraw.modes,
        },
        // styles: [
        //   // Default styles for inactive features
        //   {
        //     id: 'gl-draw-polygon-fill-inactive',
        //     type: 'fill',
        //     filter: [
        //       'all',
        //       ['==', 'active', 'false'],
        //       ['==', '$type', 'Polygon'],
        //       ['!=', 'mode', 'static'],
        //     ],
        //     paint: {
        //       'fill-color': '#088',
        //       'fill-outline-color': '#088',
        //       'fill-opacity': 0.5,
        //     },
        //   },
        //   {
        //     id: 'gl-draw-polygon-stroke-inactive',
        //     type: 'line',
        //     filter: [
        //       'all',
        //       ['==', 'active', 'false'],
        //       ['==', '$type', 'Polygon'],
        //       ['!=', 'mode', 'static'],
        //     ],
        //     layout: {
        //       'line-cap': 'round',
        //       'line-join': 'round',
        //     },
        //     paint: {
        //       'line-color': '#088',
        //       'line-width': 2,
        //     },
        //   },
        //   // Active (being drawn) styles
        //   {
        //     id: 'gl-draw-polygon-fill-active',
        //     type: 'fill',
        //     filter: [
        //       'all',
        //       ['==', 'active', 'true'],
        //       ['==', '$type', 'Polygon'],
        //     ],
        //     paint: {
        //       'fill-color': '#088',
        //       'fill-outline-color': '#088',
        //       'fill-opacity': 0.3,
        //     },
        //   },
        //   {
        //     id: 'gl-draw-polygon-stroke-active',
        //     type: 'line',
        //     filter: [
        //       'all',
        //       ['==', 'active', 'true'],
        //       ['==', '$type', 'Polygon'],
        //     ],
        //     layout: {
        //       'line-cap': 'round',
        //       'line-join': 'round',
        //     },
        //     paint: {
        //       'line-color': '#088',
        //       'line-width': 2,
        //     },
        //   },
        //   // Vertex points
        //   {
        //     id: 'gl-draw-polygon-midpoint',
        //     type: 'circle',
        //     filter: [
        //       'all',
        //       ['==', '$type', 'Point'],
        //       ['==', 'meta', 'midpoint'],
        //     ],
        //     paint: {
        //       'circle-radius': 3,
        //       'circle-color': '#088',
        //     },
        //   },
        //   {
        //     id: 'gl-draw-polygon-and-line-vertex-halo-active',
        //     type: 'circle',
        //     filter: [
        //       'all',
        //       ['==', 'meta', 'vertex'],
        //       ['==', '$type', 'Point'],
        //       ['!=', 'mode', 'static'],
        //     ],
        //     paint: {
        //       'circle-radius': 5,
        //       'circle-color': '#FFF',
        //     },
        //   },
        //   {
        //     id: 'gl-draw-polygon-and-line-vertex-active',
        //     type: 'circle',
        //     filter: [
        //       'all',
        //       ['==', 'meta', 'vertex'],
        //       ['==', '$type', 'Point'],
        //       ['!=', 'mode', 'static'],
        //     ],
        //     paint: {
        //       'circle-radius': 3,
        //       'circle-color': '#088',
        //     },
        //   },
        // ],
      });

      map.addControl(Draw, 'top-left');

      if (dataLayermapDrawMode === 'draw_rectangle') {
        map.getCanvasContainer().style.cursor = 'crosshair';
      }

      setDraw(Draw);
      if (Draw) {
        Draw.changeMode(dataLayermapDrawMode);
      }
      drawModeRef.current = dataLayermapDrawMode;

      return () => {
        try {
          map.removeControl(Draw);
          map.getCanvasContainer().style.cursor = '';
        } catch (e) {
          console.log('Error in cleanup:', e);
        }
      };
    }

    if (dataLayermapDrawMode !== 'draw_rectangle') {
      setShowClearButton(false);
      if (map) {
        map.getCanvasContainer().style.cursor = '';
      }
    }
  }, [dataLayerFD.drawMode, map]); // Remove drawModeRef and layer from dependencies

  useEffect(() => {
    // Can try using modeRef as well - if that makes this better

    if (dataLayerFD.api === 'bbox' || dataLayerFD.api === 'lowResBBOX') {
      try {
        if (draw) {
          draw.deleteAll();
          draw.changeMode(dataLayerFD.drawMode);
        }
      } catch (e) {
        console.log(e);
      }
    }
    if (dataLayerFD.api === 'lowResTile' && map) {
      map.on('click', (e) => {
        // console.log(e);
        const coord = mapboxgl.MercatorCoordinate.fromLngLat(e.lngLat, 0);
        console.log(coord);
        dispatch({
          type: setTile,
          payload: {
            tile: tilebelt.pointToTile(
              e.lngLat.lng,
              e.lngLat.lat,
              Math.round(map.getZoom()) + 1
            ),
          },
        });
      });
    }

    // This doesn't work. The else if condition should be executed only when the previous api was coverage
    if (dataLayerFD.api === 'coverage' && map) {
      // console.log('fly to 2');
      // addPlaceLabelLayers(map);
      map.flyTo({ center: [20.6, 34.1], zoom: 1.4 });
      console.log('flying in coverage ---');
    } else if (map) {
      console.log('fly to 1', localLocation);
      // removePlaceLabelLayers(map);

      // map.flyTo({
      //   center: Object.values(locationObj[dataLayerFD.type])[0].center,
      //   zoom: Object.values(locationObj[dataLayerFD.type])[0].zoom,
      // }))
      const defaultLocation = Object.values(
        dataLayerConfig?.[dataLayerFD.type]?.locations ?? {}
      )[0];

      if (map.getZoom() < 2) {
        if (localLocation) {
          map.flyTo({
            center: localLocation.center,
            // eslint-disable-next-line no-nested-ternary
            zoom: localLocation.zoom
              ? localLocation.zoom
              : mode === 'draw_coverage'
              ? 7
              : 14,
          });
        } else {
          map.flyTo({
            center: defaultLocation.center,
            zoom: defaultLocation.zoom ? defaultLocation.zoom : 14,
          });
        }
      }
    }

    if (map) {
      if (map && map.getSource('tileURL')) {
        // console.log('removing tileURL');
        map.removeLayer('tileURL');
        map.removeSource('tileURL');
      }
    }
  }, [dataLayerFD.api]);

  const polyMask = (mask, bounds) => {
    // const bboxPoly = bboxPolygon(bounds);
    // console.log({ mask, bounds });
    const maskPoly = turf.bboxPolygon(mask);
    const boundsPolyg = bounds;
    // console.log(maskPoly);
    // console.log(boundsPolyg);
    const dif = turf.difference(boundsPolyg, maskPoly);
    // console.log(dif, 'dif');
    return dif;
  };

  useEffect(() => {
    // console.log('tileURL', tileURL, tileUrlCounter);
    if (map && map.getSource('tileURL')) {
      // console.log('removing tileURL');
      map.removeLayer('tileURL');
      map.removeSource('tileURL');
    }

    if (tileURL && map && dataLayerFD.api === 'xyz') {
      // console.log('adding tileURL');
      map.addSource('tileURL', {
        type: 'raster',
        tiles: [tileURL],
        tileSize: 256,
        bounds: [
          map.getBounds()._sw.lng,
          map.getBounds()._sw.lat,
          map.getBounds()._ne.lng,
          map.getBounds()._ne.lat,
        ],
      });

      map.addLayer({
        id: 'tileURL',
        source: 'tileURL',
        type: 'raster',
        minzoom: 0,
        maxzoom: MAXZOOM,
        layout: { visibility: 'visible' },
      });
    }

    if (map && map.getSource('bbox-image')) {
      map.removeLayer('bbox-image');
      map.removeSource('bbox-image');
    }

    if (tileURL && map && dataLayerFD.api === 'bbox') {
      map.addSource('bbox-image', {
        type: 'image',
        url: tileURL,
        coordinates: [
          polygon[0][0],
          polygon[0][1],
          polygon[0][2],
          polygon[0][3],
        ],
      });

      map.addLayer({
        id: 'bbox-image',
        type: 'raster',
        source: 'bbox-image',
        paint: {
          'raster-opacity': 1,
        },
        layout: { visibility: 'visible' },
      });
    }
  }, [tileURL, tileUrlCounter]);

  // Add a basemap switcher. Take it from api-docs
  useEffect(() => {
    let localmap = null;

    getMapStyle().then((mapStyl) => {
      localmap = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyl,
        center: [lng, lat],
        zoom,
        hash: true,
        maxZoom: 17.2,
      });

      localmap.on('load', () => {
        localmap.on('zoomend', (e) => {
          setZoom(localmap.getZoom());
        });

        getMapBaseLayerList().then((mapBaseLayerList) => {
          setMapBaseLayer(mapBaseLayerList);
          addBaseMapLayers(localmap, mapBaseLayerList);
        });

        getMapPlaceLabelLayerList().then((mapPlaceLabelLayerList) => {
          setMapPlaceLabelLayer(mapPlaceLabelLayerList);
          addPlaceLabelLayers(localmap, mapPlaceLabelLayerList);
        });

        // map.showTileBoundaries = true;
        // map.showCollisionBoxes = true;

        onCreateListener = (e) => {
          // console.log('create', e.features[0]);

          if (mapDrawMode === 'draw_single_point') {
            // console.log('Point created:', e.features[0].geometry.coordinates);
            dispatch({
              type: setPoint,
              payload: {
                point: e.features[0].geometry.coordinates,
              },
            });
          }

          if (mapDrawMode === 'draw_rectangle') {
            dispatch({
              type: setPolygon,
              payload: {
                polygon: e.features[0].geometry.coordinates,
              },
            });

            // The code below creates the area popup and informative bottom label around the drawn rectangle
            // Calculate area in hectares
            const area = turf.area(e.features[0]) / 10000;

            // Get the top point of the polygon for the popup
            const topPoint = getPolygonInfoWindowPoint(e.features[0]);

            // Add the area popup and store the reference in context
            const popup = new mapboxgl.Popup({
              closeOnClick: false,
              closeButton: false,
              className: 'top-popup',
              maxWidth: '150px',
            })
              .setLngLat(topPoint)
              .setHTML(
                `<p><b>Area:</b> ${area.toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                })} ha</p>`
              )
              .addTo(localmap);

            // Store the popup reference
            setMapTopPopupRef(popup);

            // Get the bottom point for the informational popup
            const bottomPoint = getPolygonInfoWindowPoint(
              e.features[0],
              'bottom'
            );

            // Always show the informational message
            new mapboxgl.Popup({
              closeOnClick: false,
              closeButton: false,
              anchor: 'top',
              maxWidth: '500px',
              className: 'bottom-popup',
            })
              .setLngLat(bottomPoint)
              .setHTML(`ⓘ Final calculated area is based on the polygons.`)
              .addTo(localmap);

            setShowClearButton(true);
          }
        };

        localmap.on('draw.create', onCreateListener);

        dispatch({
          type: setAPIFD,
          payload: {
            api: 'point',
            drawMode: 'draw_single_point',
          },
        });
        localmap.loadImage('./Group.png', (error, image) => {
          // console.log('XXXXXXX');
          if (error) {
            console.log(error);
          } else {
            localmap.addImage('coverageMarker', image, { sdf: true });
          }
        });
      });

      localmap.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

      localmap.addControl(
        new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          mapboxgl,
          types: 'country,region,district,place,locality,postcode',
          collapsed: true,
          marker: {
            color: '#01B075',
          },
        })
      );

      setMap(localmap);
      mapRef.current = localmap;
    });

    // Clean up on unmount
    return () => localmap && localmap.remove(); // Use localmap instead of map
  }, []);

  // useEffect(() => {
  //   const mapDrawModeLocal = dataLayerFD.drawMode;
  //   // console.log('mapdrawmode: ', mapDrawModeLocal);
  //   if (map) {
  //     // console.log('mapdrawMode: map');
  //     if (onCreateListener) {
  //       map.off('draw.create', onCreateListener);
  //     }
  //     onCreateListener = (e) => {
  //       // console.log('create', e.features[0]);
  //       // console.log(mapDrawModeLocal);

  //       if (mapDrawModeLocal === 'draw_single_point') {
  //         dispatch({
  //           type: setPoint,
  //           payload: {
  //             point: e.features[0].geometry.coordinates,
  //           },
  //         });
  //       }

  //       if (mapDrawModeLocal === 'draw_rectangle') {
  //         dispatch({
  //           type: setPolygon,
  //           payload: {
  //             polygon: e.features[0].geometry.coordinates,
  //           },
  //         });

  //         // Calculate area in hectares
  //         const area = turf.area(e.features[0]) / 10000;

  //         // Get the top point of the polygon for the popup
  //         const topPoint = getPolygonInfoWindowPoint(e.features[0]);

  //         // First clear any existing popups
  //         if (mapTopPopupRef) {
  //           mapTopPopupRef.remove();
  //           setMapTopPopupRef(null);
  //         }

  //         // Add the area popup and store the reference in context
  //         const popup = new mapboxgl.Popup({
  //           closeOnClick: false,
  //           closeButton: false,
  //           className: 'top-popup',
  //           maxWidth: '150px',
  //         })
  //           .setLngLat(topPoint)
  //           .setHTML(
  //             `<p><b>Area:</b> ${area.toLocaleString('en-US', {
  //               maximumFractionDigits: 2,
  //             })} ha</p>`
  //           )
  //           .addTo(map);

  //         // Store the popup reference
  //         setMapTopPopupRef(popup);

  //         // Get the bottom point for the informational popup
  //         const bottomPoint = getPolygonInfoWindowPoint(e.features[0], 'bottom');

  //         // Always show the informational message
  //         new mapboxgl.Popup({
  //           closeOnClick: false,
  //           closeButton: false,
  //           anchor: 'top',
  //           maxWidth: '500px',
  //           className: 'bottom-popup',
  //         })
  //           .setLngLat(bottomPoint)
  //           .setHTML(
  //             `ⓘ Final calculated area is based on the polygons.`
  //           )
  //           .addTo(map);

  //         setShowClearButton(true);
  //       }
  //     };

  //     map.on('draw.create', onCreateListener);

  //     // console.log('mapdrawMode: attaching Listener', onCreateListener);

  //     map.on('draw.delete', (e) => {
  //       console.log('del', e.features[0]);

  //       // Clear any popups when a shape is deleted
  //       if (mapTopPopupRef) {
  //         mapTopPopupRef.remove();
  //         setMapTopPopupRef(null);
  //       }

  //       // Also check for any other popups via map._popups
  //       if (map._popups && map._popups.length) {
  //         const popups = [...map._popups];
  //         popups.forEach(popup => popup.remove());
  //       }

  //       // As a last resort, directly remove popup elements from the DOM
  //       const popupElements = document.getElementsByClassName('mapboxgl-popup');
  //       if (popupElements.length > 0) {
  //         Array.from(popupElements).forEach(element => {
  //           element.remove();
  //         });
  //       }

  //       // Reset the clear button state
  //       setShowClearButton(false);
  //     });
  //     map.on('draw.update', (e) => {
  //       console.log('update', e);

  //       // When a shape is updated, update the area popup
  //       if (e.features && e.features.length > 0 && e.features[0].geometry.type === 'Polygon') {
  //         // Calculate area in hectares
  //         const area = turf.area(e.features[0]) / 10000;

  //         // Clear existing popups
  //         if (mapTopPopupRef) {
  //           mapTopPopupRef.remove();
  //           setMapTopPopupRef(null);
  //         }

  //         // Get the top point of the polygon for the popup
  //         const topPoint = getPolygonInfoWindowPoint(e.features[0]);

  //         // Add the area popup and store in context
  //         const popup = new mapboxgl.Popup({
  //           closeOnClick: false,
  //           closeButton: false,
  //           className: 'top-popup',
  //           maxWidth: '150px',
  //         })
  //           .setLngLat(topPoint)
  //           .setHTML(
  //             `<p><b>Area:</b> ${area.toLocaleString('en-US', {
  //               maximumFractionDigits: 2,
  //             })} ha</p>`
  //           )
  //           .addTo(map);

  //         // Store the popup reference
  //         setMapTopPopupRef(popup);
  //       }
  //     });
  //   }
  // }, [dataLayerFD.drawMode]);

  useEffect(() => {
    const mapDrawModeLocal = dataLayerFD.drawMode;

    if (map) {
      // Set appropriate cursor
      if (mapDrawModeLocal === 'draw_rectangle') {
        map.getCanvasContainer().style.cursor = 'crosshair';
      } else {
        map.getCanvasContainer().style.cursor = '';
      }
      // Remove ALL draw event listeners to prevent duplicates
      if (onCreateListener) {
        map.off('draw.create', onCreateListener);
      }
      map.off('draw.delete');
      map.off('draw.update');

      // Clear any existing popups when mode changes
      clearAllMapPopups(map, mapTopPopupRef, setMapTopPopupRef);

      // Reset draw control to the correct mode - BUT ONLY IF draw exists
      if (draw && typeof draw.deleteAll === 'function') {
        try {
          // Only delete all if we're changing modes, not when first initializing
          if (drawModeRef.current && drawModeRef.current !== mapDrawModeLocal) {
            draw.deleteAll();
          }

          // Change to the new mode
          if (typeof draw.changeMode === 'function') {
            draw.changeMode(mapDrawModeLocal);
          }

          // Update the ref
          drawModeRef.current = mapDrawModeLocal;

          // Always reset the clear button state when API or draw mode changes
          setShowClearButton(false);
        } catch (err) {
          console.log('Draw control not ready loading');
        }
      }

      // Reset UI state based on mode
      if (mapDrawModeLocal !== 'draw_rectangle') {
        setShowClearButton(false);
      } else {
        setShowClearButton(false);
      }

      // Define the new create listener
      onCreateListener = (e) => {
        if (mapDrawModeLocal === 'draw_single_point') {
          dispatch({
            type: setPoint,
            payload: {
              point: e.features[0].geometry.coordinates,
            },
          });
        }

        if (mapDrawModeLocal === 'draw_rectangle') {
          dispatch({
            type: setPolygon,
            payload: {
              polygon: e.features[0].geometry.coordinates,
            },
          });

          // Calculate area in hectares
          const area = turf.area(e.features[0]) / 10000;
          const topPoint = getPolygonInfoWindowPoint(e.features[0]);

          clearAllMapPopups(map, mapTopPopupRef, setMapTopPopupRef);

          const popup = new mapboxgl.Popup({
            closeOnClick: false,
            closeButton: false,
            className: 'top-popup',
            maxWidth: '150px',
          })
            .setLngLat(topPoint)
            .setHTML(
              `<p><b>Area:</b> ${area.toLocaleString('en-US', {
                maximumFractionDigits: 2,
              })} ha</p>`
            )
            .addTo(map);

          setMapTopPopupRef(popup);

          const bottomPoint = getPolygonInfoWindowPoint(
            e.features[0],
            'bottom'
          );

          new mapboxgl.Popup({
            closeOnClick: false,
            closeButton: false,
            anchor: 'top',
            maxWidth: '500px',
            className: 'bottom-popup',
          })
            .setLngLat(bottomPoint)
            .setHTML(`ⓘ Final calculated area is based on the polygons.`)
            .addTo(map);

          setShowClearButton(true);
        }
      };

      // Attach the new listener
      map.on('draw.create', onCreateListener);

      // Re-attach delete handler
      map.on('draw.delete', (e) => {
        clearAllMapPopups(map, mapTopPopupRef, setMapTopPopupRef);
        setShowClearButton(false);
      });

      // Re-attach update handler
      map.on('draw.update', (e) => {
        if (
          e.features &&
          e.features.length > 0 &&
          e.features[0].geometry.type === 'Polygon'
        ) {
          const area = turf.area(e.features[0]) / 10000;
          clearAllMapPopups(map, mapTopPopupRef, setMapTopPopupRef);
          const topPoint = getPolygonInfoWindowPoint(e.features[0]);

          const popup = new mapboxgl.Popup({
            closeOnClick: false,
            closeButton: false,
            className: 'top-popup',
            maxWidth: '150px',
          })
            .setLngLat(topPoint)
            .setHTML(
              `<p><b>Area:</b> ${area.toLocaleString('en-US', {
                maximumFractionDigits: 2,
              })} ha</p>`
            )
            .addTo(map);

          setMapTopPopupRef(popup);
        }
      });
    }

    return () => {
      if (map) {
        map.off('draw.create', onCreateListener);
        map.off('draw.delete');
        map.off('draw.update');
        clearAllMapPopups(map, mapTopPopupRef, setMapTopPopupRef);
      }
    };
  }, [dataLayerFD.drawMode, dataLayerFD.api, map, draw]);

  useEffect(() => {
    let hoveredField = null;
    if (map) {
      if (map.getSource('feature')) {
        map.removeLayer('feature');
        if (map.getLayer('markers')) {
          map.removeLayer('markers');
        }
        map.removeLayer('feature-line');
        if (map.getLayer('feature-text')) {
          // The layers added to show 'View DR Image' and 'The Area is in development' in DR coverage
          map.removeLayer('feature-text');
          map.removeLayer('feature-link');
          map.removeLayer('feature-text2');
          if (map.getLayer('feature-text3')) map.removeLayer('feature-text3');
        }
        map.removeSource('feature');

        if (map.getSource('selected-feature-timeseries')) {
          map.removeLayer('selected-feature-timeseries');
          map.removeSource('selected-feature-timeseries');
        }
      }

      if (feature && feature.type) {
        console.log('adding feature', dataLayerFD.api);
        map.addSource('feature', {
          type: 'geojson',
          data: feature,
          // eslint-disable-next-line no-unneeded-ternary
          generateId: dataLayerFD.api === 'coverage' ? true : false,
        });

        // if (dataLayerFD.api === "coverage") {
        //   map.addLayer({
        //     id: "feature",
        //     type: "fill",
        //     source: "feature",
        //     paint: {
        //       "fill-color": [
        //         "match",
        //         ["get", "status"],
        //         "archived",
        //         [
        //           ["boolean", ["feature-state", "hover"], false],
        //           "rgba(234, 137, 29,0.8)",
        //           "rgba(234, 137, 29,0.3)",
        //         ],
        //         "rgba(255,255,255,0.3)",
        //       ],
        //     },
        //   });
        // } else {
        map.addLayer({
          id: 'feature',
          type: 'fill',
          source: 'feature',
          paint: {
            'fill-color': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              dataLayerFD.api === 'coverage'
                ? 'rgba(255, 255, 255, 0.8)'
                : 'rgba(255,255,255,0.3)',
              'rgba(255,255,255,0.3)',
            ],
          },
        });
        // }
        map.addLayer({
          id: 'feature-line',
          type: 'line',
          source: 'feature',
          paint: {
            'line-width': dataLayerFD.api === 'coverage' ? 3 : 2,
            'line-color': [
              'match',
              ['get', 'status'],
              'archived',
              '#EA891D',
              '#fff',
            ],
          },
        });

        if (
          dataLayerFD.api === 'coverage' &&
          (dataLayerFD.type === 'delineatedFields' ||
            dataLayerFD.type === 'timeSeries')
        ) {
          // feature.features && console.log(feature.features);
          const paintStyle =
            dataLayerFD.type === 'delineatedFields'
              ? {
                  'icon-color': [
                    'match', // Use the 'match' expression: https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-match
                    ['get', 'delineation_status'], // Use the result 'STORE_TYPE' property
                    'done',
                    '#01B075',
                    'in development',
                    '#FF8C00',
                    '#FF0000',
                  ],
                }
              : { 'icon-color': '#01B075' };

          map.addLayer({
            id: 'markers',
            type: 'symbol',
            source: 'feature',
            minzoom: 0,
            maxzoom: 5.4,
            layout: {
              'icon-image': 'coverageMarker',
            },
            paint: paintStyle,
          });
          map.on('click', 'markers', (e) => {
            console.log(e.features[0].id);

            if (map.getZoom() < 5.4) {
              map.flyTo({ center: e.lngLat, zoom: 5.4 });
            }
          });
        } else if (
          dataLayerFD.api === 'coverage' &&
          dataLayerFD.type !== 'delineatedFields'
        ) {
          map.addLayer({
            id: 'feature-text',
            type: 'symbol',
            source: 'feature',
            layout: {
              'text-field': ['get', 'mgrs_tile'],
              'text-justify': 'center',
              'text-anchor': 'top',
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                0,
                2,
                0,
                4,
                6,
                6,
                12,
                8,
                14,
              ],
            },
            paint: {
              'text-color': '#fff',
            },
          });
          map.addLayer({
            id: 'feature-link',
            type: 'symbol',
            source: 'feature',
            layout: {
              'text-justify': 'center',
              'text-anchor': 'top',
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                0,
                2,
                0,
                4,
                6,
                6,
                12,
                8,
                14,
              ],
              'text-font': ['Open Sans Bold'],
            },
            paint: {
              'text-color': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                '#01B075',
                'rgba(0, 152, 200, 0)',
              ],
              'text-translate': [0, -20],
            },
            filter: ['!=', ['get', 'status'], 'upcoming'],
          });
          map.addLayer({
            id: 'feature-text2',
            type: 'symbol',
            source: 'feature',
            layout: {
              'text-field': [
                'format',
                'Archived',
                { 'font-scale': 1.2 },
                '\n\n',
                { 'font-scale': 0.4 },
                ['get', 'mgrs_tile'],
                {
                  'font-scale': 0.9,
                },
              ],

              'text-justify': 'center',
              'text-anchor': 'top',
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                0,
                2,
                0,
                4,
                6,
                6,
                12,
                8,
                14,
              ],
              'text-font': ['Open Sans Bold'],
            },
            paint: {
              'text-color': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                '#EA891D',
                'rgba(0, 152, 200, 0)',
              ],
              'text-translate': [0, -20],
            },

            filter: ['==', ['get', 'status'], 'archived'],
          });

          map.addLayer({
            id: 'feature-text3',
            type: 'symbol',
            source: 'feature',
            layout: {
              'text-field': [
                'format',
                'Available',
                { 'font-scale': 1.2 },
                '\n\n',
                { 'font-scale': 0.4 },
                ['get', 'mgrs_tile'],
                {
                  'font-scale': 0.9,
                },
              ],

              'text-justify': 'center',
              'text-anchor': 'top',
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                0,
                2,
                0,
                4,
                6,
                6,
                12,
                8,
                14,
              ],
              'text-font': ['Open Sans Bold'],
            },
            paint: {
              'text-color': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                '#01B075',
                'rgba(0, 152, 200, 0)',
              ],
              'text-translate': [0, -20],
            },

            filter: ['==', ['get', 'status'], 'ready'],
          });

          map.on('mousemove', 'feature', (e) => {
            const layerId = 'feature';
            if (e.features.length > 0) {
              if (hoveredField) {
                map.setFeatureState(
                  { source: layerId, id: hoveredField },
                  { hover: false }
                );
              }
              const newHoveredField = e.features[0].id;
              map.setFeatureState(
                { source: 'feature', id: newHoveredField },
                { hover: true }
              );
              hoveredField = newHoveredField;
            }
          });
          map.on('mouseleave', 'feature', (e) => {
            if (hoveredField) {
              map.setFeatureState(
                { source: 'feature', id: hoveredField },
                { hover: false }
              );
            }
            hoveredField = null;
          });
        }
        map.on('click', 'feature', (e) => {
          if (
            dataLayerFD.api === 'lowResBBOX' ||
            dataLayerFD.api === 'lowResTile'
          ) {
            console.log(e.features);
            dispatch({
              type: setFieldId,
              payload: {
                fieldId: e.features[0].properties.id,
              },
            });

            if (dataLayerFD.type === 'timeSeries') {
              if (map.getSource('selected-feature-timeseries')) {
                map.removeLayer('selected-feature-timeseries');
                map.removeSource('selected-feature-timeseries');
              }
              map.addSource('selected-feature-timeseries', {
                type: 'geojson',
                data: {
                  ...feature,
                  features: feature.features.filter(
                    (feat) => feat.id === e.features[0].properties.id
                  ),
                },
              });

              map.addLayer({
                id: 'selected-feature-timeseries',
                type: 'fill',
                source: 'selected-feature-timeseries',
                paint: {
                  'fill-color': 'rgba(234, 137, 29, 0.8)',
                },
              });
            }
          } else if (
            mode === 'draw_coverage' &&
            dataLayerFD.type === 'delineatedFields'
          ) {
            if (map.getZoom() < 5.4) {
              map.flyTo({ center: e.lngLat, zoom: 5.4 });
            }
          } else if (
            mode === 'draw_coverage' &&
            dataLayerFD.type === 'deepResImages'
          ) {
            // Needs to be source date.
            const availableDates =
              e.features[0].properties.availableDates &&
              JSON.parse(e.features[0].properties.availableDates);
            if (availableDates && availableDates[0].s3_cog) {
              const s2FileName = availableDates[0].s3_cog.split('//')[1];
              const center = turf.center(e.features[0].geometry);
              const s2L2AFileName = s2FileName
                .replace('/dr/', '/L2A/')
                .replace('S2_DR_T', 'S2_L2A_')
                .replace('S2_DR', 'S2_L2A')
                .replace('_cog.tif', '_rgb_cog.tif');
              console.log(s2FileName, s2L2AFileName);

              const fileUrl = `https://demo-time-slider.s3.eu-central-1.amazonaws.com/index.html?leftFilename=${s2L2AFileName}&rightFilename=${s2FileName}&leftNames=L2AFile&rightNames=Deeply Resolved#14/${center.geometry.coordinates[1]}/${center.geometry.coordinates[0]}`;
              window.open(fileUrl, '_blank');
            }
          }
        });

        if (dataLayerFD.api === 'lowResBBOX') {
          try {
            draw.deleteAll();
          } catch (e) {
            console.log(e);
          }
        }
      }
    }
  }, [feature]);

  useEffect(() => {
    if (
      map &&
      map.isStyleLoaded() && // Add this check
      point &&
      point.length &&
      dataLayerFD.drawMode === 'draw_single_point'
    ) {
      // Remove existing custom point layer/source if present
      if (map.getLayer('custom-point')) {
        map.removeLayer('custom-point');
      }
      if (map.getSource('custom-point')) {
        map.removeSource('custom-point');
      }

      // Add new source
      map.addSource('custom-point', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(point[0]), parseFloat(point[1])],
              },
            },
          ],
        },
      });

      // Add new layer with your color
      map.addLayer({
        id: 'custom-point',
        type: 'circle',
        source: 'custom-point',
        paint: {
          'circle-radius': 4,
          'circle-color': '#01B075', // your desired color
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
        },
      });
    }

    // Optional: Clean up on mode change or unmount
    return () => {
      if (map && map.isStyleLoaded()) {
        if (map.getLayer('custom-point')) {
          map.removeLayer('custom-point');
        }
        if (map.getSource('custom-point')) {
          map.removeSource('custom-point');
        }
      }
    };
  }, [map, point, dataLayerFD.drawMode]);

  // This is to set the draw rectangle on the map
  useEffect(() => {
    // console.log('polygon0000', polygon);

    if (
      draw &&
      polygon &&
      polygon.length &&
      dataLayerFD.drawMode === 'draw_rectangle'
    ) {
      const featureCollection = draw.getAll();
      // console.log('polygon0000', polygon);

      if (!featureCollection.features.length) {
        featureCollection.features = [
          {
            type: 'Feature',
            geometry: { coordinates: [], type: 'Polygon' },
            properties: {},
          },
        ];
      }

      featureCollection.features[0].geometry.coordinates = polygon;
      draw.set(featureCollection);
    }
  }, [polygon]);

  useEffect(() => {
    if (map && location) {
      // console.log('fly to location', location);
      map.flyTo({
        center: location.center,
        // eslint-disable-next-line no-nested-ternary
        zoom: location.zoom ? location.zoom : mode === 'draw_coverage' ? 7 : 14,
      });
    }
  }, [location]);

  useEffect(() => {
    if (map && dataLayerFD && mapBaseLayer.length) {
      if (
        dataLayerFD.type === 'delineatedFields' ||
        dataLayerFD.type === 'timeSeries'
      ) {
        turnOnBaseMapLayers(map);
      } else {
        turnOffBaseMapLayers(map);
      }
    }
  }, [dataLayerFD, map, mapBaseLayer]);

  const toggleBaseLayer = () => {
    if (map.getLayer('basemap_sat')) {
      const visibiltyofSatLayer = map.getLayoutProperty(
        'basemap_sat',
        'visibility'
      );
      if (visibiltyofSatLayer === 'visible') {
        map.setLayoutProperty('basemap_basic', 'visibility', 'visible');
        map.setLayoutProperty('basemap_sat', 'visibility', 'none');
      } else {
        map.setLayoutProperty('basemap_basic', 'visibility', 'none');
        map.setLayoutProperty('basemap_sat', 'visibility', 'visible');
      }
    }
  };

  useEffect(() => {
    if (map) map.resize();
  }, [isRightDrawerOpen]);

  useEffect(() => {
    // Maybe once we need to do this without panning. i.e immediately after the mode is changed
    // console.log('dataLayer/mode changed', dataLayerFD);
    if (
      dataLayerFD.api === 'xyz' &&
      map &&
      dataLayerFD.type === 'deepResImages'
    ) {
      const tilesArray = getTilesForCurrentBounds(map);
      const tilesArr = [
        tilesArray.map((tile) => tile[0]),
        tilesArray.map((tile) => tile[1]),
        tilesArray[0][2],
      ];
      // console.log(tilesArray);
      dispatch({
        type: setTiles,
        payload: {
          tiles: tilesArr,
        },
      });

      map.on('moveend', (e) => {
        // console.log(e);
        if (
          dataLayerFD.api === 'xyz' &&
          map &&
          dataLayerFD.type === 'deepResImages'
        ) {
          const tilesArrList = getTilesForCurrentBounds(map);
          // console.log(tilesArrList);
          const tilesList = [
            tilesArray.map((tile) => tile[0]),
            tilesArray.map((tile) => tile[1]),
            tilesArray[0][2],
          ];

          dispatch({
            type: setTiles,
            payload: {
              tiles: tilesList,
            },
          });
        }
      });
    }
  }, [dataLayerFD]);

  useEffect(() => {
    if (map) {
      const geojsonSource = map.getSource('selected-feature-timeseries');
      if (map.getSource('selected-feature-timeseries') && !fieldId) {
        map.removeLayer('selected-feature-timeseries');
        map.removeSource('selected-feature-timeseries');
      }
    }
  }, [fieldId]);

  const removeMGRSExpectionLayer = () => {
    if (map && map.getSource('mgrsException-feature')) {
      map.removeLayer('mgrsException-layer');
      map.removeLayer('mgrsException-line');
      map.removeSource('mgrsException-feature');
    }
  };

  return (
    <div className={classes.mapAndControlsContainer}>
      <div className={classes.mapContainer} ref={mapContainerRef} />
      {/* <div className={classes.globeIconContainer}>
        <Paper>
          <IconButton onClick={toggleBaseLayer}>
            <LineGlobeIcon classes={{ root: classes.iconRoot }} />
          </IconButton>
        </Paper>
      </div> */}
      <div className={classes.clearButtonContainer}>
        {showClearButton && (
          <Button
            variant="contained"
            color="primary"
            className={classes.clearButton}
            onClick={() => {
              // Use the clearAllMapPopups utility function
              clearAllMapPopups(map, mapTopPopupRef, setMapTopPopupRef);

              // Then delete the drawing
              draw.deleteAll();

              // Reactivate the current mode instead of forcing rectangle mode
              // This preserves the user's selected query type
              if (draw && dataLayerFD && dataLayerFD.drawMode) {
                draw.changeMode(dataLayerFD.drawMode);
              }

              // Set showClearButton to false
              setShowClearButton(false);
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* <div className={classes.drawerToggleContainer}>
        <IconButton
          onClick={toggleRightDrawer}
          className={classes.collapseDrawerButton}
        >
          {isRightDrawerOpen ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </div> */}
      <div
        className={
          isRightDrawerOpen
            ? classes.drawerToggleMobileOpen
            : classes.drawerToggleMobileClosed
        }
      >
        <IconButton
          onClick={toggleRightDrawer}
          className={classes.collapseDrawerButtonMobile}
        >
          {isRightDrawerOpen ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
        </IconButton>
      </div>
    </div>
  );
};

export default Map;
